import { cn } from '../../utils/cn';
import { Banner } from '../Banner/Banner';
import { ChatBubble } from '../ChatBubble/ChatBubble';

/**
 * F43 (Track D design-pass) — Read-only scoped chat-moderation thread.
 *
 * Renders the ±N message window around a reported (anchor) `chat_message` for
 * the admin report-detail page (`ChatBubble` exists web-side but had no
 * read-only moderation wrapper — anchor highlight + "logged" banner +
 * show-deleted stub; see plan §6 design flag 2 + §2.7).
 *
 * The message shape is defined LOCALLY (the ui-web package deliberately does
 * NOT depend on `@bravemove/types` — every component carries its own prop types).
 * `ChatModerationThreadMessage` is structurally compatible with `@bravemove/types`'
 * `ChatContextMessage`, so a caller may pass a `ChatContextMessage[]` directly.
 *
 * Production component: a prominent warning `Banner` (read-is-logged copy), a
 * scrollable read-only list of `ChatBubble` (one per message; system messages
 * styled distinctly; soft-deleted rows shown as a muted italic stub with the
 * body still visible to the admin), the anchor message visually highlighted
 * (ring + "reported" marker + `data-anchor`). No composer, no interactivity
 * beyond scroll. Token-driven (no raw hex); logical-properties for RTL.
 *
 * The exported prop signature is FROZEN ({ messages, anchorMessageId,
 * bannerText, className? } + `ChatModerationThreadMessage`) so Track B can
 * build against it.
 */
export interface ChatModerationThreadMessage {
  readonly id: string;
  readonly roomId: string;
  readonly senderUserId: string | null;
  readonly senderDisplayName: string | null;
  /** 'text' | 'poll' | 'booking_link' | 'system' — kept as a string for structural compat with @bravemove/types' ChatMessageType. */
  readonly type: string;
  readonly body: string | null;
  readonly createdAt: string;
  readonly isAnchor: boolean;
  readonly deletedAt: string | null;
  /** 'sender' | 'admin' | 'system' | … — string for structural compat. */
  readonly deletedByRole: string | null;
}

export interface ChatModerationThreadProps {
  /** The ±N window (anchor included). Render order is as supplied (chronological). */
  readonly messages: readonly ChatModerationThreadMessage[];
  /** The reported (anchor) message id — highlighted in the thread. */
  readonly anchorMessageId: string;
  /** The "Moderation view — every read is logged" banner copy. */
  readonly bannerText: string;
  readonly className?: string;
}

function DeletedStub({
  body,
  deletedByRole,
}: {
  readonly body: string | null;
  readonly deletedByRole: string | null;
}) {
  return (
    <span className="italic text-ink-3">
      <span className="me-1.5 text-[10px] uppercase tracking-wide font-mono text-danger">
        {`(deleted${deletedByRole ? ` by ${deletedByRole}` : ''})`}
      </span>
      {body ?? ''}
    </span>
  );
}

export function ChatModerationThread({
  messages,
  anchorMessageId,
  bannerText,
  className,
}: ChatModerationThreadProps) {
  return (
    <div className={cn('flex flex-col gap-2 min-w-0', className)}>
      <Banner tone="warning" className="font-mono text-[12.5px]">
        {bannerText}
      </Banner>

      <div className="flex flex-col gap-2 py-2 overflow-y-auto max-h-[60vh] pe-1">
        {messages.length === 0 ? (
          <p className="text-center text-[12.5px] text-ink-3 py-4">No messages in this window.</p>
        ) : null}

        {messages.map((m) => {
          const isAnchor = m.id === anchorMessageId;
          const isDeleted = m.deletedAt !== null;
          const isSystem = m.type === 'system';

          // System messages render via ChatBubble's distinct system variant.
          if (isSystem) {
            return (
              <div
                key={m.id}
                data-anchor={isAnchor || undefined}
                data-message-id={m.id}
                className={cn('flex justify-center', isAnchor && 'relative')}
              >
                {isAnchor ? (
                  <span className="absolute -top-2 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 text-[9px] font-mono uppercase tracking-wider text-danger bg-surface px-1.5 rounded">
                    Reported
                  </span>
                ) : null}
                <ChatBubble variant="system">
                  {isDeleted ? (
                    <DeletedStub body={m.body} deletedByRole={m.deletedByRole} />
                  ) : (
                    (m.body ?? '')
                  )}
                </ChatBubble>
              </div>
            );
          }

          return (
            <div
              key={m.id}
              data-anchor={isAnchor || undefined}
              data-message-id={m.id}
              className={cn(
                'relative rounded-lg',
                isAnchor && 'ring-2 ring-danger/50 bg-danger/5 p-1.5 ps-2',
              )}
            >
              {isAnchor ? (
                <span className="absolute -top-2 start-2 text-[9px] font-mono uppercase tracking-wider text-danger bg-surface px-1.5 rounded">
                  Reported
                </span>
              ) : null}
              <ChatBubble
                variant="received"
                senderName={m.senderDisplayName ?? 'Unknown'}
                timestamp={m.createdAt}
              >
                {isDeleted ? (
                  <DeletedStub body={m.body} deletedByRole={m.deletedByRole} />
                ) : (
                  (m.body ?? '')
                )}
              </ChatBubble>
            </div>
          );
        })}
      </div>
    </div>
  );
}
